import { StrictMode, lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import './sentry'; // must come before any app code so unhandled errors are captured
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { ColorModeProvider } from './colorMode';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { Layout } from './components/Layout';
import { EntryList } from './pages/EntryList';
import { EntryDetail } from './pages/EntryDetail';
import { AuditLog } from './pages/AuditLog';
import { FailedLogins } from './pages/FailedLogins';
import { Import } from './pages/Import';
import { Account } from './pages/Account';
import { Settings } from './pages/Settings';
import { Dashboard } from './pages/Dashboard';
import { Metrics } from './pages/Metrics';
import { SubmitChange } from './pages/SubmitChange';
import { ChangeRequests } from './pages/ChangeRequests';
import { Login } from './pages/auth/Login';
import { PasswordReset } from './pages/auth/PasswordReset';
import { GettingStarted } from './pages/auth/GettingStarted';
import { Users } from './pages/Users';
import { EmailLog } from './pages/EmailLog';
import { EmailTemplates } from './pages/EmailTemplates';
import { Onboarding } from './pages/Onboarding';
import { OnboardingReview } from './pages/OnboardingReview';
import { ExportExcel } from './pages/ExportExcel';
import { ImageExport } from './pages/ImageExport';
import { CritSchedule } from './pages/CritSchedule';
import { Touchscreens } from './pages/Touchscreens';
import { ExportWbd } from './pages/ExportWbd';
import { ChangedExport } from './pages/ChangedExport';
import { EntrantHome } from './pages/EntrantHome';
import { RoleHome } from './components/RoleHome';
import { UnmatchedRoute } from './components/UnmatchedRoute';
import { LoadingScreen } from './components/LoadingScreen';
import './index.css';

// World Buildings Directory preview — its own chunk, only loaded when opened.
const DirectoryApp = lazy(() => import('./pages/directory/DirectoryApp'));

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ColorModeProvider>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Public — no Layout. */}
            <Route path="/login" element={<Login />} />
            <Route path="/password-reset" element={<PasswordReset />} />
            <Route path="/getting-started" element={<GettingStarted />} />

            {/* World Buildings Directory preview: its own site chrome (no portal
                Layout), but behind login since it reads the portal's API. */}
            <Route
              path="/directory/*"
              element={
                <ProtectedRoute requiredRole={['Admin', 'User', 'PR']}>
                  <Suspense fallback={<LoadingScreen />}>
                    <DirectoryApp />
                  </Suspense>
                </ProtectedRoute>
              }
            />

            {/* Authenticated — Layout renders nav chrome. */}
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Routes>
                      <Route path="/" element={<RoleHome />} />

                      {/* Staff console — Admin (super) and User (regular
                          admin) only. Entrants pasting these URLs get
                          bounced via "/" → RoleHome → /entrant. */}
                      <Route
                        path="/dashboard"
                        element={
                          <ProtectedRoute requiredRole={['Admin', 'User']}>
                            <Dashboard />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/entries"
                        element={
                          <ProtectedRoute requiredRole={['Admin', 'User', 'PR']}>
                            <EntryList />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/metrics"
                        element={
                          <ProtectedRoute requiredRole="Admin">
                            <Metrics />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/entry/:uuid"
                        element={
                          <ProtectedRoute requiredRole={['Admin', 'User', 'PR']}>
                            <EntryDetail />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/change-requests"
                        element={
                          <ProtectedRoute requiredRole={['Admin', 'User']}>
                            <ChangeRequests />
                          </ProtectedRoute>
                        }
                      />

                      {/* Super-admin only. */}
                      <Route
                        path="/audit"
                        element={
                          <ProtectedRoute requiredRole="Admin">
                            <AuditLog />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/import"
                        element={
                          <ProtectedRoute requiredRole={['Admin', 'User']}>
                            <Import />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/users"
                        element={
                          <ProtectedRoute requiredRole={['Admin', 'User']}>
                            <Users />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/failed-logins"
                        element={
                          <ProtectedRoute requiredRole="Admin">
                            <FailedLogins />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/email-log"
                        element={
                          <ProtectedRoute requiredRole={['Admin', 'User']}>
                            <EmailLog />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/email-templates"
                        element={
                          <ProtectedRoute requiredRole={['Admin', 'User']}>
                            <EmailTemplates />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/onboarding"
                        element={
                          <ProtectedRoute requiredRole="Admin">
                            <Onboarding />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/review-onboarding"
                        element={
                          <ProtectedRoute requiredRole={['Admin', 'User']}>
                            <OnboardingReview />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/export-excel"
                        element={
                          <ProtectedRoute requiredRole={['Admin', 'User']}>
                            <ExportExcel />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/image-export"
                        element={
                          <ProtectedRoute requiredRole={['Admin', 'User', 'PR']}>
                            <ImageExport />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/crit-schedule"
                        element={
                          <ProtectedRoute requiredRole={['Admin', 'User']}>
                            <CritSchedule />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/touchscreens"
                        element={
                          <ProtectedRoute requiredRole="Admin">
                            <Touchscreens />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/export-wbd"
                        element={
                          <ProtectedRoute requiredRole="Admin">
                            <ExportWbd />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/export-wbd-changed"
                        element={
                          <ProtectedRoute requiredRole="Admin">
                            <ChangedExport />
                          </ProtectedRoute>
                        }
                      />

                      {/* Entrant-only self-service flows. */}
                      <Route
                        path="/entrant"
                        element={
                          <ProtectedRoute requiredRole="Entrant">
                            <EntrantHome />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/submit-change"
                        element={
                          <ProtectedRoute requiredRole="Entrant">
                            <SubmitChange />
                          </ProtectedRoute>
                        }
                      />
                      {/* Open to any authenticated user. */}
                      <Route path="/account" element={<Account />} />
                      <Route path="/settings" element={<Settings />} />

                      <Route path="*" element={<UnmatchedRoute />} />
                    </Routes>
                  </Layout>
                </ProtectedRoute>
              }
            />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </ColorModeProvider>
  </StrictMode>
);
